<template>
  <!-- Desktop -->
  <div v-if="suppliers.length" class="hidden md:flex w-full items-center h-[32px] py-2 relative gap-4">
    <!-- Visible buttons -->
    <div ref="containerRef" class="flex items-center overflow-hidden no-scrollbar flex-1 relative">
      <!-- Actual visible buttons -->
      <button
        v-for="supplier in visibleSuppliers"
        :key="supplier"
        :id="'order-template-supplier-filter-button'" 
        @click="selectSupplier(supplier)"
        :class="[baseBtn, supplier === activeSupplier ? activeBtn : inactiveBtn]"
      >
        {{ supplier }}
      </button>
    </div>

    <!-- Dropdown menu -->
    <div v-if="overflowSuppliers.length" class="relative">
      <button id="order-template-supplier-filter-more-button"
        @click.stop="toggleDropdown"
        class="h-[32px] w-[32px] flex items-center justify-center bg-[#F8F8F8] rounded focus:outline-none"
      >
        <i class="ri-more-fill font-18 opacity-100 text-[#6E6E6E]"></i>
      </button>

      <div
        v-if="showDropdown"
        class="absolute right-0 top-full mt-2 w-max bg-white border border-[#D1D1D1] rounded z-[9999]"
      >
        <ul class="p-3 space-y-2">
          <li
            v-for="supplier in overflowSuppliers"
            :key="supplier"
            @click="selectSupplier(supplier)"
            class="cursor-pointer flex gap-2 items-center"
          >
            <span
              class="w-2 h-2 rounded-full"
              :class="supplier === activeSupplier ? 'bg-[#762123]' : 'bg-[#464646]'"
            ></span>

            <span
              :class="
                supplier === activeSupplier
                  ? 'text-[#762123] font-semibold text-base'
                  : 'text-[#464646] text-base'
              "
            >
              {{ supplier }}
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <!-- MOBILE -->
  <div class="md:hidden w-full flex items-center overflow-x-auto no-scrollbar h-[32px]">
    <button
      v-for="supplier in suppliers"
      :key="supplier"
      :id="'order-template-supplier-filter-button'"
      @click="selectSupplier(supplier)"
      :class="[baseBtn, supplier === activeSupplier ? activeBtn : inactiveBtn]"
    >
      {{ supplier }}
    </button>
  </div>

  <!-- Hidden measuring container -->
  <div ref="measureRef" class="absolute opacity-0 pointer-events-none -z-50 flex overflow-hidden h-0">
    <button
      v-for="supplier in suppliers"
      :key="supplier"
      :id="'order-template-supplier-filter-button'"
      class="order-template-btn h-[32px] px-[12px] py-[4px] whitespace-nowrap border"
    >
      {{ supplier }}
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed, onBeforeUnmount } from 'vue';

const props = defineProps({
  suppliers: Array,
  modelValue: String
});

const emit = defineEmits(['update:modelValue']);

const containerRef = ref(null);
const measureRef = ref(null);
const visibleSuppliers = ref([]);
const overflowSuppliers = ref([]);
const showDropdown = ref(false);
const supplierWidths = ref([]);

/* ------------- Stable selector classes -------------- */
const baseBtn = "order-template-btn h-[32px] px-[12px] py-[4px] whitespace-nowrap border text-[#545454] focus:outline-none focus:ring-0";
const activeBtn = "shadow-[inset_0px_3px_12px_0px_#00000059,inset_0px_4px_4px_0px_#00000040] text-[#762123] focus:outline-none focus:ring-0";
const inactiveBtn = "bg-[#F8F8F8] hover:bg-gray-200";

//VERY STABLE: WAIT UNTIL ELEMENT EXISTS
const waitForElement = async (refValue) => {
  return new Promise((resolve) => {
    const check = () => {
      if (refValue.value) resolve(true);
      else requestAnimationFrame(check);
    };
    check();
  });
};

//MEASURE HIDDEN BUTTON WIDTHS
const measureWidths = () => {
  if (!measureRef.value) return;
  supplierWidths.value = Array.from(measureRef.value.children).map((btn) =>
    btn.getBoundingClientRect().width
  );
};

//COMPUTE WHICH BUTTONS FIT
const computeVisible = () => {
  if (!containerRef.value) return;

  const containerWidth = containerRef.value.getBoundingClientRect().width || 0;

  let total = 0;
  let cutoff = props.suppliers.length;

  for (let i = 0; i < supplierWidths.value.length; i++) {
    if (total + supplierWidths.value[i] > containerWidth - 30) {
      cutoff = i;
      break;
    }
    total += supplierWidths.value[i];
  }

  visibleSuppliers.value = props.suppliers.slice(0, cutoff);
  overflowSuppliers.value = props.suppliers.slice(cutoff);
};

const recompute = async () => {
  await nextTick();
  if (!measureRef.value || !containerRef.value) return;
  measureWidths();
  computeVisible();
};

let resizeObserver;

onMounted(async () => {
  await waitForElement(containerRef);
  await waitForElement(measureRef);

  await recompute();

  resizeObserver = new ResizeObserver(() => {
    recompute();
  });

  resizeObserver.observe(containerRef.value);
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
});

const activeSupplier = computed(() => props.modelValue || props.suppliers?.[0] || "");

const selectSupplier = (value) => {
  emit("update:modelValue", value);
  showDropdown.value = false;
};

watch(() => props.suppliers, recompute, { deep: true });
const toggleDropdown = () => (showDropdown.value = !showDropdown.value);
</script>